import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import type {
  CBCFormData,
  CBCRelation,
  EnsembleCBC,
} from "../../types/ensemble.types";
import { deleteEnsembleModelCbcRelation } from "../../../../services/ensembleModelling.service";
import deleteFile from "../../../../components/Icons/deleteFile.svg";
import { FiTrash2 } from "react-icons/fi";

// Keep the details panel constrained so just the form scrolls instead of the entire page.
const PANEL_MAX_HEIGHT = "calc(100vh - 220px)";

export interface CBCDetailsPanelStatus {
  type: "success" | "error";
  message: string;
}

export type CBCDetailsPanelMode = "edit" | "create";

export interface CBCDetailsPanelProps {
  mode?: CBCDetailsPanelMode;
  selectedCBC: EnsembleCBC | null;
  onSave: (data: CBCFormData) => void | Promise<void>;
  onCancel: () => void;
  isSaving?: boolean;
  status?: CBCDetailsPanelStatus | null;
  modelName?: string;
  modelSubjectArea?: string;
  cbcs: EnsembleCBC[];
  relations: CBCRelation[];
  onRelationDeleted?: (relationId: number) => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
  isDeleting?: boolean;
  className?: string;
}

const defaultFormData: CBCFormData = {
  modelName: "",
  subjectArea: "",
  cbcName: "",
  ensembleName: "",
  businessKeys: "",
  description: "",
  type: "person",
};

export const CBCDetailsPanel: React.FC<CBCDetailsPanelProps> = ({
  mode = "edit",
  selectedCBC,
  onSave,
  onCancel,
  isSaving = false,
  status = null,
  modelName,
  modelSubjectArea,
  cbcs,
  relations,
  onRelationDeleted,
  onDelete,
  isDeleting = false,
  className = "",
}) => {
  const isCreateMode = mode === "create";
  const [formData, setFormData] = useState<CBCFormData>(defaultFormData);
  const [showDependencies, setShowDependencies] = useState(false);
  const [deletingRelationId, setDeletingRelationId] = useState<number | null>(
    null
  );
  const [deleteStatus, setDeleteStatus] = useState<
    { type: "success" | "error"; message: string } | null
  >(null);

  const formatType = (value: string | undefined) => {
    if (!value) return "Core Business Concept";
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  useEffect(() => {
    if (!selectedCBC) {
      setFormData({
        ...defaultFormData,
        modelName: modelName ?? "",
        subjectArea: modelSubjectArea ?? "",
      });
      return;
    }

    setFormData({
      modelName: modelName ?? "",
      subjectArea: modelSubjectArea ?? "",
      cbcName: selectedCBC.ensemble_model_cbc_name,
      ensembleName: selectedCBC.ensemble_name,
      businessKeys: selectedCBC.business_keys,
      description: selectedCBC.description,
      type: selectedCBC.ensemble_model_cbc_type,
    });
  }, [modelName, modelSubjectArea, selectedCBC, mode]);

  useEffect(() => {
    if (!selectedCBC && isCreateMode) {
      setFormData((prev) => ({
        ...prev,
        modelName: modelName ?? "",
        subjectArea: modelSubjectArea ?? "",
      }));
    }
  }, [isCreateMode, modelName, modelSubjectArea, selectedCBC]);

  type DependencyEntry = {
    relationId: number;
    cbc: EnsembleCBC;
    relationDescription: string | undefined;
  };

  const dependencySummary = useMemo(() => {
    if (!selectedCBC) {
      return { incoming: [], outgoing: [] } as {
        incoming: DependencyEntry[];
        outgoing: DependencyEntry[];
      };
    }

    const cbcById = new Map<number, EnsembleCBC>(
      cbcs.map((cbc) => [cbc.ensemble_model_cbc_id, cbc])
    );

    const incoming = relations
      .filter(
        (relation) =>
          relation.tgt_ensemble_model_cbc_id ===
          selectedCBC.ensemble_model_cbc_id
      )
      .map((relation) => {
        const source = cbcById.get(relation.src_ensemble_model_cbc_id);
        if (!source) {
          return null;
        }
        return {
          relationId: relation.ensemble_model_cbc_relation_id,
          cbc: source,
          relationDescription: relation.relation_description,
        };
      })
      .filter(
        (entry): entry is DependencyEntry =>
          entry !== null
      );

    const outgoing = relations
      .filter(
        (relation) =>
          relation.src_ensemble_model_cbc_id ===
          selectedCBC.ensemble_model_cbc_id
      )
      .map((relation) => {
        const target = cbcById.get(relation.tgt_ensemble_model_cbc_id);
        if (!target) {
          return null;
        }
        return {
          relationId: relation.ensemble_model_cbc_relation_id,
          cbc: target,
          relationDescription: relation.relation_description,
        };
      })
      .filter(
        (entry): entry is DependencyEntry =>
          entry !== null
      );

    return { incoming, outgoing };
  }, [cbcs, relations, selectedCBC]);

  useEffect(() => {
    setShowDependencies(false);
    setDeleteStatus(null);
    setDeletingRelationId(null);
  }, [isCreateMode, selectedCBC?.ensemble_model_cbc_id]);

  useEffect(() => {
    if (!showDependencies) {
      setDeleteStatus(null);
      setDeletingRelationId(null);
      return () => undefined;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowDependencies(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [showDependencies]);

  const totalDependencies =
    dependencySummary.incoming.length + dependencySummary.outgoing.length;

  const cardClass =
    "tw-group tw-flex tw-flex-col tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-4 tw-shadow-none tw-transition-colors focus-within:tw-border-[#03234a]/40";
  const cardHaloClass = "tw-hidden";
  const labelClass =
    "tw-text-xs tw-font-semibold tw-text-slate-600";
  const inputClass =
    "tw-w-full tw-appearance-none tw-rounded-lg tw-border tw-border-[#e3e8f2] tw-bg-white tw-px-3.5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-slate-900 tw-shadow-[0_0_0_1px_rgba(226,231,242,0.7)] tw-transition placeholder:tw-text-slate-400 focus:tw-border-[#4f658c] focus:tw-shadow-[0_0_0_1px_rgba(79,101,140,0.35)] focus:tw-outline-none focus:tw-ring-0";
                                                                                                                          
  const relationCardClass =
    "tw-group tw-relative tw-overflow-hidden tw-rounded-[1.75rem] tw-border tw-border-[#03234a]/12 tw-bg-gradient-to-br tw-from-[#f7f9ff] tw-via-white tw-to-[#edf3ff] tw-p-5 tw-shadow-[0_24px_42px_-30px_rgba(3,35,74,0.55)] tw-transition-all tw-duration-400 hover:tw--translate-y-1 hover:tw-shadow-[0_30px_54px_-28px_rgba(3,35,74,0.55)] focus-within:tw-border-[#03234a]/30 focus-within:tw-shadow-[0_34px_60px_-30px_rgba(3,35,74,0.6)]";
  const relationHaloClass =
    "tw-pointer-events-none tw-absolute tw-inset-0 tw-rounded-[1.75rem] tw-bg-gradient-to-br tw-from-[#03234a]/18 tw-via-[#6b8db9]/16 tw-to-transparent tw-opacity-0 tw-blur-lg tw-transition-all tw-duration-400 group-hover:tw-opacity-100 group-focus-within:tw-opacity-100";

  if (!selectedCBC && !isCreateMode) {
    return (
      <aside
        className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-rounded-l-3xl tw-border-l tw-border-slate-200 tw-bg-white tw-text-slate-400"
        style={{ maxHeight: PANEL_MAX_HEIGHT }}
      >
        <p className="tw-text-center tw-text-sm tw-font-medium">
          Select a Core Business Concept node to view its details.
        </p>
      </aside>
    );
  }

  const handleChange = (
    key: keyof CBCFormData,
    value: CBCFormData[typeof key]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleRelationDelete = async (relationId: number) => {
    if (!relationId || deletingRelationId !== null) {
      return;
    }

    setDeletingRelationId(relationId);
    setDeleteStatus(null);

    try {
      await deleteEnsembleModelCbcRelation(relationId);
      if (onRelationDeleted) {
        await onRelationDeleted(relationId);
      }
      setDeleteStatus({
        type: "success",
        message: "Dependency removed successfully.",
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to remove dependency.";
      setDeleteStatus({
        type: "error",
        message,
      });
    } finally {
      setDeletingRelationId(null);
    }
  };

  return (
    <aside
      className={`tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-[1.75rem] tw-border tw-border-slate-100 tw-bg-white tw-shadow-[0_30px_80px_-50px_rgba(15,23,42,0.45)] ${className}`}
    >
      <div className="tw-flex tw-flex-col tw-gap-4 tw-border-b tw-border-slate-200 tw-p-6" style={{ paddingBottom: "1rem" }}>
        <div>
          <h2 className="tw-text-xl tw-font-semibold tw-text-slate-900">
            {isCreateMode ? "Create concept" : "Edit concept"}
          </h2>
          <p className="tw-mt-1 tw-text-sm tw-text-slate-500">
            {isCreateMode ? (
              "Add a new Core Business Concept to this model."
            ) : (
              <>
                Editing{" "}
                <strong className="tw-text-slate-700">
                  {selectedCBC?.ensemble_model_cbc_name ?? ""}
                </strong>
              </>
            )}
          </p>
        </div>
        {!isCreateMode && selectedCBC && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2.5">
            <button
              type="button"
              onClick={() => setShowDependencies(true)}
              className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-bg-[#03234a] tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-transition hover:tw-bg-[#0a3f7f] focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-[#03234a]/60"
            >
              View relationships
              <span className="tw-inline-flex tw-min-w-[1.4rem] tw-items-center tw-justify-center tw-rounded-full tw-bg-white/20 tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold">
                {totalDependencies}
              </span>
            </button>
            {onDelete && (
              <button
                type="button"
                onClick={() => {
                  if (isDeleting) return;
                  onDelete();
                }}
                disabled={isDeleting}
                className={`tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-border-rose-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-rose-600 tw-transition focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-rose-200 ${
                  isDeleting
                    ? "tw-cursor-not-allowed tw-opacity-60"
                    : "hover:tw-bg-rose-50"
                }`}
              >
                <FiTrash2 className="tw-h-4 tw-w-4" />
                {isDeleting ? "Deleting…" : "Delete"}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="tw-flex tw-flex-1 tw-min-h-0 tw-flex-col tw-gap-6 tw-p-6">
        <div className="tw-flex-1 tw-overflow-y-auto tw-space-y-6">
          {status && (
            <div
              className={`tw-rounded-lg tw-border tw-p-4 tw-text-sm ${
                status.type === "success"
                  ? "tw-border-emerald-200 tw-bg-emerald-50 tw-text-emerald-700"
                  : "tw-border-red-200 tw-bg-red-50 tw-text-red-600"
              }`}
            >
              {status.message}
            </div>
          )}

          <div className="tw-grid tw-gap-6 md:tw-grid-cols-2">
            <div className={cardClass}>
              <span className={cardHaloClass} aria-hidden />
              <label className={labelClass}>Subject Area</label>
              <input
                value={formData.subjectArea}
                onChange={(event) =>
                  handleChange("subjectArea", event.target.value)
                }
                placeholder="Enter subject area"
                className={inputClass}
              />
            </div>
            <div className={cardClass}>
              <span className={cardHaloClass} aria-hidden />
              <label className={labelClass}>Ensemble Name</label>
              <input
                value={formData.ensembleName}
                onChange={(event) =>
                  handleChange("ensembleName", event.target.value)
                }
                placeholder="Name this ensemble view"
                className={inputClass}
              />
            </div>
          </div>

          <div className={cardClass}>
            <span className={cardHaloClass} aria-hidden />
            <label className={labelClass}>Concept name</label>
            <input
              value={formData.cbcName}
              onChange={(event) =>
                handleChange("cbcName", event.target.value)
              }
              placeholder="Enter concept title"
              className={inputClass}
            />
          </div>

          <div className={cardClass}>
            <span className={cardHaloClass} aria-hidden />
            <label className={labelClass}>Business Keys</label>
            <input
              value={formData.businessKeys}
              onChange={(event) =>
                handleChange("businessKeys", event.target.value)
              }
              placeholder="Comma separated identifiers"
              className={inputClass}
            />
          </div>

          <div className={cardClass}>
            <span className={cardHaloClass} aria-hidden />
            <label className={labelClass}>Description</label>
            <textarea
              value={formData.description}
              onChange={(event) =>
                handleChange("description", event.target.value)
              }
              rows={3}
              placeholder="What does this concept represent?"
              className={`${inputClass} tw-min-h-[8rem] tw-resize-y`}
            />
          </div>

          <div className={cardClass}>
            <span className={cardHaloClass} aria-hidden />
            <label className={labelClass}>Type</label>
            <select
              value={formData.type}
              onChange={(event) =>
                handleChange("type", event.target.value as CBCFormData["type"])
              }
              className={`${inputClass} tw-pr-10`}
            >
              <option value="person">Person</option>
              <option value="event">Event</option>
              <option value="thing">Thing</option>
              <option value="place">Place</option>
            </select>
          </div>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-3 md:tw-flex-row md:tw-items-center md:tw-justify-between">
          <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row md:tw-flex-1 md:tw-justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="tw-flex-1 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-600 tw-transition hover:tw-border-slate-300 hover:tw-bg-slate-100 sm:tw-flex-none sm:tw-w-32"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSave(formData)}
              disabled={isSaving}
              className={`tw-flex-1 tw-rounded-lg tw-bg-[#03234a] tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-transition hover:tw-bg-[#0a3f7f] hover:tw-shadow-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-[#03234a]/60 sm:tw-flex-none sm:tw-w-32 ${
                isSaving
                  ? "tw-opacity-60 tw-cursor-not-allowed hover:tw-bg-[#03234a]"
                  : ""
              }`}
            >
              {isSaving
                ? isCreateMode
                  ? "Creating..."
                  : "Saving..."
                : isCreateMode
                ? "Create"
                : "Save"}
            </button>
          </div>
        </div>

      </div>

      {!isCreateMode &&
        showDependencies &&
        selectedCBC &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="tw-fixed tw-inset-0 tw-z-[1200] tw-flex tw-items-center tw-justify-center tw-p-4"
            role="dialog"
            aria-modal="true"
            aria-label="Core Business Concept dependency overview"
          >
            <div
              className="tw-absolute tw-inset-0 tw-bg-[#03234a]/60 tw-backdrop-blur-md tw-transition-opacity tw-duration-300"
              onClick={() => setShowDependencies(false)}
            />
            <div className="tw-relative tw-z-10 tw-flex tw-h-full tw-w-full tw-max-h-[90vh] tw-max-w-5xl tw-flex-col tw-overflow-hidden tw-rounded-[2.5rem] tw-border tw-border-[#03234a]/20 tw-bg-gradient-to-br tw-from-white tw-via-slate-50 tw-to-blue-50 tw-p-8 tw-text-[#03234a] tw-shadow-[0_42px_72px_-28px_rgba(3,35,74,0.65)] md:tw-p-12">
              <div className="tw-flex tw-flex-col tw-gap-6 md:tw-flex-row md:tw-items-start md:tw-justify-between">
                <div>
                  <p className="tw-text-[0.55rem] tw-font-semibold tw-uppercase tw-tracking-[0.45em] tw-text-[#03234a]/70">
                    Relationship Matrix
                  </p>
                  <h3 className="tw-mt-3 tw-text-2xl tw-font-semibold">
                    {selectedCBC.ensemble_model_cbc_name}
                  </h3>
                  <p className="tw-mt-2 tw-max-w-lg tw-text-sm tw-font-medium tw-text-[#03234a]/70">
                    Visualize how this concept flows across upstream parents and
                    downstream children for rapid impact analysis.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDependencies(false)}
                  className="tw-self-end tw-rounded-full tw-border tw-border-[#03234a]/20 tw-bg-white/80 tw-px-4 tw-py-2 tw-text-[0.6rem] tw-font-semibold tw-uppercase tw-tracking-[0.35em] tw-text-[#03234a] tw-shadow-[0_16px_30px_-22px_rgba(3,35,74,0.35)] tw-transition-all tw-duration-300 hover:tw--translate-y-0.5 hover:tw-shadow-[0_22px_38px_-24px_rgba(3,35,74,0.45)] focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-[#03234a]/60"
                >
                  Close
                </button>
              </div>

              {deleteStatus && (
                <div
                  className={`tw-mt-4 tw-rounded-2xl tw-border tw-px-5 tw-py-3 tw-text-sm tw-font-medium ${
                    deleteStatus.type === "success"
                      ? "tw-border-emerald-300/60 tw-bg-emerald-50/80 tw-text-emerald-700"
                      : "tw-border-rose-300/60 tw-bg-rose-50/80 tw-text-rose-700"
                  }`}
                >
                  {deleteStatus.message}
                </div>
              )}

              <div className="tw-mt-6 tw-flex-1 tw-overflow-y-auto tw-pr-2">
                <div className="tw-grid tw-gap-6 md:tw-grid-cols-2">
                  <section className="tw-flex tw-flex-col tw-gap-4">
                    {dependencySummary.incoming.length === 0 ? (
                      <div className="tw-rounded-2xl tw-border tw-border-[#03234a]/10 tw-bg-white/70 tw-px-5 tw-py-6 tw-text-sm tw-font-semibold tw-text-[#03234a]/60">
                        No dependencies recorded yet.
                      </div>
                    ) : (
                      <ul className="tw-grid tw-gap-5">
                        {dependencySummary.incoming.map(({ relationId, cbc, relationDescription }) => {
                          const isDeleting = deletingRelationId === relationId;
                          return (
                            <li key={`incoming-${relationId}`} className={relationCardClass}>
                              <span className={relationHaloClass} aria-hidden />
                              <div className="tw-relative tw-z-10">
                                <div className="tw-flex tw-items-center tw-justify-end">
                                  <button
                                    type="button"
                                    onClick={() => handleRelationDelete(relationId)}
                                    disabled={isDeleting}
                                    className={`tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-transparent tw-bg-[#fdf2f5] tw-transition-all tw-duration-200 hover:tw-bg-rose-100 hover:tw-shadow-[0_10px_18px_-12px_rgba(225,29,72,0.55)] focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-rose-200 ${
                                      isDeleting
                                        ? "tw-cursor-not-allowed tw-opacity-50"
                                        : ""
                                    }`}
                                    aria-label={`Remove relation with ${cbc.ensemble_model_cbc_name}`}
                                  >
                                    <img
                                      src={deleteFile}
                                      alt=""
                                      className="tw-h-3.5 tw-w-3.5"
                                    />
                                  </button>
                                </div>
                                <div className="tw-mt-3 tw-space-y-3">
                                  <div className="tw-rounded-2xl tw-border tw-border-[#03234a]/10 tw-bg-[#eef4ff] tw-px-4 tw-py-4 tw-text-[#03234a] tw-shadow-[0_18px_36px_-24px_rgba(3,35,74,0.35)]">
                                    <p className="tw-text-[0.55rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-[#03234a]/65">
                                      Relation Note
                                    </p>
                                    <p className="tw-mt-1 tw-text-sm tw-font-semibold tw-text-[#011833]">
                                      {relationDescription?.trim() || "No relation note added yet."}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="tw-text-[0.55rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-[#03234a]/55">
                                      Concept
                                    </p>
                                    <p className="tw-text-sm tw-font-semibold tw-text-[#03234a]">
                                      {cbc.ensemble_model_cbc_name}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="tw-text-[0.55rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-[#03234a]/55">
                                      Ensemble
                                    </p>
                                    <p className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-[0.25em] tw-text-[#03234a]/60">
                                      {cbc.ensemble_name || "—"}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="tw-text-[0.55rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-[#03234a]/55">
                                      Type
                                    </p>
                                    <p className="tw-text-xs tw-uppercase tw-tracking-[0.25em] tw-text-[#03234a]/65">
                                      {formatType(cbc.ensemble_model_cbc_type)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </section>

                  <section className="tw-flex tw-flex-col tw-gap-4">
                    {dependencySummary.outgoing.length === 0 ? (
                      <div className="tw-rounded-2xl tw-border tw-border-[#03234a]/10 tw-bg-white/70 tw-px-5 tw-py-6 tw-text-sm tw-font-semibold tw-text-[#03234a]/60">
                        No dependencies recorded yet.
                      </div>
                    ) : (
                      <ul className="tw-grid tw-gap-5">
                        {dependencySummary.outgoing.map(({ relationId, cbc, relationDescription }) => {
                          const isDeleting = deletingRelationId === relationId;
                          return (
                            <li key={`outgoing-${relationId}`} className={relationCardClass}>
                              <span className={relationHaloClass} aria-hidden />
                              <div className="tw-relative tw-z-10">
                                <div className="tw-flex tw-items-center tw-justify-end">
                                  <button
                                    type="button"
                                    onClick={() => handleRelationDelete(relationId)}
                                    disabled={isDeleting}
                                    className={`tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-transparent tw-bg-[#fdf2f5] tw-transition-all tw-duration-200 hover:tw-bg-rose-100 hover:tw-shadow-[0_10px_18px_-12px_rgba(225,29,72,0.55)] focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-rose-200 ${
                                      isDeleting
                                        ? "tw-cursor-not-allowed tw-opacity-50"
                                        : ""
                                    }`}
                                    aria-label={`Remove relation with ${cbc.ensemble_model_cbc_name}`}
                                  >
                                    <img
                                      src={deleteFile}
                                      alt=""
                                      className="tw-h-3.5 tw-w-3.5"
                                    />
                                  </button>
                                </div>
                                <div className="tw-mt-3 tw-space-y-3">
                                  <div className="tw-rounded-2xl tw-border tw-border-[#03234a]/10 tw-bg-[#eef4ff] tw-px-4 tw-py-4 tw-text-[#03234a] tw-shadow-[0_18px_36px_-24px_rgba(3,35,74,0.35)]">
                                    <p className="tw-text-[0.55rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-[#03234a]/65">
                                      Relation Note
                                    </p>
                                    <p className="tw-mt-1 tw-text-sm tw-font-semibold tw-text-[#011833]">
                                      {relationDescription?.trim() || "No relation note added yet."}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="tw-text-[0.55rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-[#03234a]/55">
                                      Concept
                                    </p>
                                    <p className="tw-text-sm tw-font-semibold tw-text-[#03234a]">
                                      {cbc.ensemble_model_cbc_name}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="tw-text-[0.55rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-[#03234a]/55">
                                      Ensemble
                                    </p>
                                    <p className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-[0.25em] tw-text-[#03234a]/60">
                                      {cbc.ensemble_name || "—"}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="tw-text-[0.55rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-[#03234a]/55">
                                      Type
                                    </p>
                                    <p className="tw-text-xs tw-uppercase tw-tracking-[0.25em] tw-text-[#03234a]/65">
                                      {formatType(cbc.ensemble_model_cbc_type)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </section>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </aside>
  );
};

export default CBCDetailsPanel;
